/**
 * 属性面板纯逻辑（P0-4）——输入解析/镜像/落地/角度归一全部离线可测，
 * UI 组件只做渲染与转发，不夹带计算（同 cameraMath 的分层原则）。
 */
import type { Transform } from '../../model/director';

/** 解析输入框文本：非法/空/Infinity 一律回退 fallback，绝不把 NaN 写进工程数据 */
export function parseNum(raw: string, fallback: number): number {
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : fallback;
}

/** 角度收进 (-180, 180]：旋转编辑与镜像都要用，防止度数无限累加 */
export function normalizeDeg(deg: number): number {
  if (!Number.isFinite(deg)) {
    return 0;
  }
  let d = deg % 360;
  if (d > 180) {
    d -= 360;
  } else if (d <= -180) {
    d += 360;
  }
  return d;
}

/**
 * 沿世界 X=0 平面镜像：位置 x 翻号；朝向 yaw 取 180−yaw（面对镜头变背对镜头的镜像语义）；
 * roll（z）翻号。y 轴旋转在欧拉序下的精确镜像依赖旋转序，v1 取"角色摆位"够用的近似，
 * 精确姿态镜像属 P0-6 姿势层（左右骨互换）的课题——不冒充完美。
 */
export function mirrorTransform(t: Transform): Transform {
  const [x, y, z] = t.position;
  const [rx, ry, rz] = t.rotation;
  return {
    position: [-x, y, z],
    rotation: [rx, normalizeDeg(180 - ry), -rz],
    scale: [...t.scale] as Transform['scale'],
  };
}

/** 一键落地：y 归零（素体/几何体的脚底都在 origin，工程数据即真相） */
export function groundTransform(t: Transform): Transform {
  const [x, , z] = t.position;
  return {
    position: [x, 0, z],
    rotation: [...t.rotation] as Transform['rotation'],
    scale: [...t.scale] as Transform['scale'],
  };
}

/** 显示用取整（3 位小数），避免 0.30000000000000004 进输入框 */
export function round3(v: number): number {
  return Number(v.toFixed(3));
}

/** 数值钳制（输入框 min/max 之外的值直接钳回，不弹错不打断） */
export function clampNum(v: number, min: number, max: number): number {
  if (!Number.isFinite(v)) {
    return min;
  }
  return Math.min(max, Math.max(min, v));
}
